import { Card, CardBody } from "@chakra-ui/react";
import AppPage from "layouts/AppPage";
import { useParams } from "react-router";
import { taskKeys, useTaskDetailQuery } from "features/Resources/Task/hooks";
import QueryError from "components/Error/QueryError";
import { usePlatformEventCable, CollapsibleHistory } from "features/PlatformEvents";
import TaskForm from "features/Resources/Task/Layout/TaskForm";
import ClosedTask from "features/Resources/Task/Layout/ClosedTask";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import CaminoBreadcrumbs from "components/Breadcrumbs";
import { INTERNAL_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { newHireJourneyPathById } from "helpers/url";
import { truncateEnd } from "helpers/string";
import TaskActionModalByQueryParam from "features/Resources/Task/Components/Action/TaskActionModalByQueryParam";

export default function TaskPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error: queryError, isError } = useTaskDetailQuery(id);
  const { data: newHireJourney, isFetched } = useNewHireJourneyDetailQuery(data?.newHireJourneyId, {
    enabled: !!data?.newHireJourneyId,
  });

  usePlatformEventCable({
    entityType: "Task",
    entityId: data?.id,
    enabled: !!data?.id,
    detailQueryKeys: data?.id ? taskKeys.detail(data.id) : undefined,
  });

  if (!id) {
    return <AppPage>Task not found</AppPage>;
  }

  if (isLoading) {
    return <AppPage>Loading...</AppPage>;
  }

  if (!data && isError) {
    return <QueryError queryError={queryError} resourceName="Task" id={id} />;
  }

  const isCustomTask = data?.taskType === "custom";

  const breadcrumbInfos = isCustomTask
    ? [{ name: "My Tasks", toUrl: "/tasks" }, { name: `Task '${truncateEnd(data?.name)}'` }]
    : [
        { name: "New Hire Journeys", toUrl: INTERNAL_TEAM_ROUTES.newHireJourneys.index },
        {
          name: isFetched && newHireJourney?.newHire.fullName ? `${newHireJourney.newHire.fullName}` : "Onboarding",
          toUrl: newHireJourneyPathById(newHireJourney?.id),
        },
        { name: `Task '${truncateEnd(data?.name)}'` },
      ];

  // Prefer the assignee's timezone so the start date displays in their local
  // calendar — falls back to the new hire's timezone (template-derived tasks
  // are typically authored in that context) and finally the viewer's browser
  // timezone if neither is set.
  const taskTimezone =
    data?.assignee?.timezone ??
    newHireJourney?.newHire?.timezone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const taskIsPending = isCustomTask ? false : (newHireJourney?.isPending ?? false);

  return (
    <AppPage>
      <CaminoBreadcrumbs breadcrumbInfos={breadcrumbInfos} />
      {data && (
        <Card overflow="hidden">
          <CardBody>
            {["skipped", "completed"].includes(data.status) ? (
              <ClosedTask task={data} timezone={taskTimezone} />
            ) : (
              <TaskForm
                data={data}
                includeHeader
                includeMenu
                includeReminders
                isPending={taskIsPending}
                timezone={taskTimezone}
              />
            )}
            <CollapsibleHistory entityId={data.id} entityType="Task" eventType="task" />
          </CardBody>
        </Card>
      )}
      <TaskActionModalByQueryParam />
    </AppPage>
  );
}
